import { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle2, Quote } from 'lucide-react';
import { authApi } from '../services/api';
import { toast } from 'sonner';

interface AdminLoginProps {
  onLogin: () => void;
}

type View = 'login' | 'forgot';

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const [view, setView] = useState<View>('login');

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Reset step state (code from the email + the new password)
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const backToLogin = () => {
    setView('login');
    setForgotSent(false);
    setForgotEmail('');
    setResetOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await authApi.login(email.trim().toLowerCase(), password.trim());
      if (response.success && response.token) {
        toast.success('Login successful! Welcome back.');
        onLogin();
      } else {
        setError(response.message || 'Invalid email or password');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password. Please try again.');
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail.trim().toLowerCase());
      setForgotSent(true);
      toast.success('Reset code sent! Check your inbox.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetOtp.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      await authApi.resetPassword(forgotEmail.trim().toLowerCase(), resetOtp, newPassword);
      toast.success('Password updated! Sign in with your new password.');
      backToLogin();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-white">
      {/* ── Left hero panel ───────────────────────────── */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#2B7A7E] via-[#22696d] to-[#0f3739]">
        {/* decorative shapes */}
        <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#0f3739]/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16 text-white">
          {/* brand */}
          <div className="flex flex-col items-center text-center">
            <img src="/logo.png" alt="TOTD" className="h-24 w-auto object-contain mb-4" />
            <span className="text-xl font-semibold tracking-tight">Thought of the Day</span>
          </div>

          {/* quote */}
          <div className="max-w-md mx-auto text-center">
            <Quote className="w-10 h-10 text-white/30 mx-auto mb-5" />
            <p className="text-2xl xl:text-3xl font-medium leading-snug">
              Every day is a new page. Make it count.
            </p>
            <p className="mt-6 text-white/60">
              Manage daily thoughts, users, and content — all from one place.
            </p>
          </div>

          <p className="text-center text-white/50 text-sm">© 2024 TOTD App. All rights reserved.</p>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-4 py-10 sm:px-6 overflow-y-auto">
        <div className="w-full max-w-md my-auto">
          {/* mobile brand */}
          <div className="lg:hidden flex flex-col items-center text-center mb-8">
            <img src="/logo.png" alt="TOTD Logo" className="h-24 w-auto object-contain mb-3" />
            <span className="text-slate-500 text-sm font-medium">Thought of the Day</span>
          </div>

          {view === 'login' ? (
            <>
              <div className="mb-7">
                <h1 className="text-3xl font-bold text-slate-900">Sign in</h1>
                <p className="text-slate-500 mt-1">Welcome back — enter your details to continue.</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-900">Authentication Failed</p>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@totd.app"
                      required
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-slate-700">Password</label>
                    <button
                      type="button"
                      onClick={() => { setView('forgot'); setError(''); }}
                      className="text-sm text-[#2B7A7E] hover:text-[#1e5a5d] font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full pl-11 pr-11 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-[#2B7A7E] text-white rounded-xl font-semibold shadow-lg shadow-[#2B7A7E]/25 hover:bg-[#1e5a5d] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing in...
                    </>
                  ) : 'Sign In'}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={backToLogin}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>

              {forgotSent ? (
                <>
                  <div className="mb-6">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">Check your email</h3>
                    <p className="text-sm text-slate-500">
                      If an account exists for <strong className="text-slate-700">{forgotEmail}</strong>, we've sent a
                      6-digit code. It expires in 15 minutes.
                    </p>
                  </div>

                  <form onSubmit={handleResetPassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Reset Code
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={resetOtp}
                        onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        required
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-center text-2xl font-semibold tracking-[0.4em] placeholder:text-slate-300 placeholder:tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="At least 6 characters"
                          required
                          className="w-full pl-11 pr-11 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter new password"
                          required
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="w-full py-3.5 bg-[#2B7A7E] text-white rounded-xl font-semibold shadow-lg shadow-[#2B7A7E]/25 hover:bg-[#1e5a5d] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {resetLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Updating...
                        </>
                      ) : 'Set New Password'}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setForgotSent(false); setResetOtp(''); setNewPassword(''); setConfirmPassword(''); }}
                      className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Didn't get the code? Send it again
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">Reset Password</h3>
                    <p className="text-sm text-slate-500">Enter your email and we'll send you a 6-digit reset code.</p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="admin@totd.app"
                          required
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E]/40 focus:border-[#2B7A7E] focus:bg-white transition-all"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full py-3.5 bg-[#2B7A7E] text-white rounded-xl font-semibold shadow-lg shadow-[#2B7A7E]/25 hover:bg-[#1e5a5d] active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {forgotLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : 'Send Reset Link'}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          <p className="lg:hidden text-center text-slate-400 text-xs mt-8">
            © 2024 TOTD App. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
